import { ReactNode, useState } from "react";

import { Button } from "atoms/button/Button";

import { ActionListPicker } from "../components/ActionListPicker/ActionListPicker";
import OnboardingHeaderV3 from "../components/OnboardingScreenHeader/OnboardingHeaderV3";

interface GenderItem {
  id: string;
  label: string;
}

interface GenderPickerScreenProps {
  onGenderSelect?: (genderId: string) => void | Promise<void>;
  selectedGender?: string;
  className?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
  submitButtonText?: string;
}

export function GenderPickerScreen({
  onGenderSelect,
  selectedGender = "",
  className = "",
  prefix,
  suffix,
  submitButtonText = "Continue",
}: Readonly<GenderPickerScreenProps>) {
  const [selectedId, setSelectedId] = useState<string>(selectedGender);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);

  const genderOptions: GenderItem[] = [
    { id: "female", label: "Woman" },
    { id: "male", label: "Man" },
    { id: "non-binary", label: "Non-binary" },
    { id: "prefer-not-to-say", label: "Prefer not to share" },
  ];

  const handleSelectionChange = (selectedIds: string[]) => {
    const newSelectedId = selectedIds[0] ?? "";
    setSelectedId(newSelectedId);
    // Pass the selected ID directly to avoid race condition with state updates
    if (newSelectedId) {
      handleContinue(newSelectedId);
    }
  };

  const handleContinue = async (genderIdOverride?: string) => {
    // Use override if provided (from selection), otherwise use state (from button click)
    const genderId = genderIdOverride ?? selectedId;
    if (!genderId || !onGenderSelect) return;

    setIsSubmitting(true);
    setSubmitError(undefined);

    try {
      await onGenderSelect(genderId);
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : "An error occurred. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasSelection = !!selectedId;

  return (
    <div className={`flex w-full max-w-sm flex-col gap-9 ${className}`}>
      {/* Optional prefix slot for logo */}
      {prefix ? (
        <div className="flex w-full items-center justify-center">{prefix}</div>
      ) : null}

      {/* Header */}
      <OnboardingHeaderV3 title="What's your gender?" align="left" />

      {/* Gender options */}
      <div className="flex flex-col gap-3">
        <ActionListPicker<GenderItem>
          items={genderOptions}
          multiSelect={false}
          selectedIds={selectedGender ? [selectedGender] : []}
          onSelectionChange={handleSelectionChange}
        />

        {/* Submit Error */}
        {submitError && (
          <div className="mb-4 text-center text-sm text-red">{submitError}</div>
        )}

        {/* Continue Button */}
        <div className="mt-6">
          <Button
            fullWidth
            disabled={!hasSelection || isSubmitting}
            onClick={() => handleContinue()}
          >
            {isSubmitting ? "Loading..." : submitButtonText}
          </Button>
        </div>
      </div>

      {/* Optional suffix slot */}
      {suffix ? <div className="w-full">{suffix}</div> : null}
    </div>
  );
}
